import React, { useEffect, useState } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  getDoc,
} from "firebase/firestore";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import {
  HiOutlineXMark,
  HiOutlineUserGroup,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";

interface AddMemberModalProps {
  ministryId: string;
  organizationId: string;
  onClose: () => void;
}

const glassBtnMuted =
  "w-full py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors";

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  ministryId,
  organizationId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const filteredUsers = users.filter((user) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
    const email = (user.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(
        collection(db, "users"),
        where("organizationId", "==", organizationId),
        where("role", "==", "member"),
      );
      const querySnapshot = await getDocs(q);
      setUsers(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    };

    const fetchMinistryMembers = async () => {
      const ministryDoc = await getDoc(doc(db, "ministries", ministryId));
      if (ministryDoc.exists()) setMembers(ministryDoc.data().members || []);
    };

    fetchUsers();
    fetchMinistryMembers();
  }, [organizationId, ministryId]);

  const handleAddMember = async (userId: string) => {
    setBusyId(userId);
    try {
      await updateDoc(doc(db, "ministries", ministryId), {
        members: arrayUnion(userId),
      });
      setMembers((prev) => [...prev, userId]);
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    setBusyId(userId);
    try {
      await updateDoc(doc(db, "ministries", ministryId), {
        members: arrayRemove(userId),
      });
      setMembers((prev) => prev.filter((id) => id !== userId));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineUserGroup className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {t("addRemoveMembers") || "Add / Remove members"}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {members.length} {t("members")?.toLowerCase() || "members"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="relative">
            <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchMembers") || "Search by name or email"}
              autoFocus
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
            />
          </div>
        </div>
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <Skeleton count={5} />
          ) : filteredUsers.length === 0 ? (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
              {users.length === 0
                ? t("noMembersFound")
                : t("noSearchResults") || "No matches"}
            </p>
          ) : (
            <ul className="space-y-2">
              {filteredUsers.map((user) => {
                const isMember = members.includes(user.id);
                const busy = busyId === user.id;
                return (
                  <li
                    key={user.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200/70 dark:border-gray-700/70 bg-white/50 dark:bg-gray-900/25"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {user.firstName} {user.lastName}
                      </p>
                      {user.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        isMember
                          ? handleRemoveMember(user.id)
                          : handleAddMember(user.id)
                      }
                      className={`shrink-0 min-h-[40px] px-3 py-2 rounded-xl text-sm font-medium border transition-colors disabled:opacity-60 ${
                        isMember
                          ? "border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-red-600 dark:text-red-300"
                          : "border-[#5932EA]/35 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd]"
                      }`}
                    >
                      {busy ? "…" : isMember ? t("remove") : t("add")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <button type="button" onClick={onClose} className={glassBtnMuted}>
            {t("close")}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default AddMemberModal;
